import React from "react";
import { sparklePath } from "./expressions";

// ── Sparkle descriptor for big-eye mode ──
interface SparkleItem {
  dx: number;
  dy: number;
  s: number;
}

// ── Plain eye types (union) ──
interface PlainEyeBig {
  type: "big";
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  lidTop?: number;
  sparkles?: SparkleItem[];
}

interface PlainEyeArc {
  type: "arc";
  cx?: number;
  cy?: number;
  d: string;
}

interface PlainEyeCircle {
  type: "circle";
  cx: number;
  cy: number;
  r: number;
}

export type PlainEyeDescriptor = PlainEyeBig | PlainEyeArc | PlainEyeCircle;

interface PlainEyeProps {
  eye: PlainEyeDescriptor;
  color: string;
  bgColor: string;
  t: string;
  clipId: string;
}

export const PlainEye: React.FC<PlainEyeProps> = ({
  eye,
  color,
  bgColor,
  t,
  clipId,
}) => {
  if (eye.type === "arc") {
    return (
      <path
        d={eye.d}
        fill="none"
        stroke={color}
        strokeWidth={3.5}
        strokeLinecap="round"
        style={{ transition: t }}
      />
    );
  }

  if (eye.type === "big") {
    const lidTopY = eye.cy - eye.ry + eye.ry * 2 * (eye.lidTop || 0);
    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect
              x={eye.cx - eye.rx - 2}
              y={eye.lidTop && eye.lidTop > 0 ? lidTopY : eye.cy - eye.ry - 3}
              width={(eye.rx + 2) * 2}
              height={eye.ry * 2 + 6}
              style={{ transition: t }}
            />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <ellipse
            cx={eye.cx}
            cy={eye.cy}
            rx={eye.rx}
            ry={eye.ry}
            fill={color}
            style={{ transition: t }}
          />
          {(eye.sparkles || []).map((sp, i) => (
            <path
              key={i}
              d={sparklePath(eye.cx + sp.dx, eye.cy + sp.dy, sp.s)}
              fill="white"
              opacity={0.92}
              style={{ transition: t }}
            />
          ))}
        </g>
      </g>
    );
  }

  // type === "circle"
  return (
    <circle
      cx={eye.cx}
      cy={eye.cy}
      r={eye.r}
      fill={color}
      style={{ transition: t }}
    />
  );
};

// ── Plain mouth types (union) ──
interface PlainMouthStroke {
  type: "stroke";
  d: string;
  sw: number;
}

interface PlainMouthFill {
  type: "fill";
  d: string;
}

interface PlainMouthRing {
  type: "ring";
  cx: number;
  cy: number;
  r: number;
}

export type PlainMouthDescriptor =
  | PlainMouthStroke
  | PlainMouthFill
  | PlainMouthRing;

interface PlainMouthProps {
  mouth: PlainMouthDescriptor;
  color: string;
  t: string;
}

export const PlainMouth: React.FC<PlainMouthProps> = ({ mouth, color, t }) => {
  if (mouth.type === "stroke") {
    return (
      <path
        d={mouth.d}
        fill="none"
        stroke={color}
        strokeWidth={mouth.sw}
        strokeLinecap="round"
        style={{ transition: t }}
      />
    );
  }

  if (mouth.type === "fill") {
    return (
      <path
        d={mouth.d}
        fill={color}
        opacity={0.85}
        style={{ transition: t }}
      />
    );
  }

  if (mouth.type === "ring") {
    return (
      <circle
        cx={mouth.cx}
        cy={mouth.cy}
        r={mouth.r}
        fill="none"
        stroke={color}
        strokeWidth={3}
        style={{ transition: t }}
      />
    );
  }

  return null;
};
